import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import "./Layout.css";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { useChatbot, useClickOutside, usePlant, useSocket, useUserDetail, useUserPermissions } from "../../services/hooks";
import { AssistantIcon, CloseIcon, RightArrowIcon, SearchIcon, SendIcon } from "../../services/icons";
import { Dialog, Dropdown, Input, Loader, toastMessage } from "../utils/index";
import { useChangePasswordMutation, useLogoutMutation, useUpdateTourStatusMutation } from "../../api/userSlice";
import { useApplytheme } from "../../services/hooks";
import { socket } from "../../socket";
import { apiSlice } from "../../api/api";
import { useBranding } from "../../context/brandingContext";
import GuidedTour from "../Tour/Tour";
import { useSidebarItems } from "../../context/sidebarContext";
import { useTour } from "../../context/tourContext";
import { usePlantContext } from "../../context/plantContext";
import VehicleSearchDropdown from "./VehicleSearchDropdown";
import ConsentPopup from "../ConsentPopup";
import MenuIcon from "@mui/icons-material/Menu";
export default function Layout({ children }) {
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const { pathname } = useLocation();
	useEffect(() => setIsSidebarOpen(false), [pathname]);
	useSocket();
	const { logo } = useApplytheme();
	// fetch all plants of user after login; all subsequence calls to this hook in any other component need to be without passing in filter object, to avoid unnecessary api calls
	usePlant({});
	const { userDetail, isFetching, refetch } = useUserDetail();
	// useOfflineTemplate();
	// const isOnline = useCheckInternet();

	const windowType = localStorage.getItem("windowType") || false;
	const activeUrl = localStorage.getItem("activeURL") || false;
	const showSidebar = localStorage.getItem("showSidebar") === "false" ? false : true;

	// Allow travel
	// if (windowType === "windowOpener" && activeUrl !== location.pathname) {
	// 	navigate("/not-authorized");
	// }

	const isNotWhiteLabel = window.location.hostname.includes("lynkid") || window.location.hostname.includes("localhost");
	
	if (isFetching) {
		return <Loader size="2.5rem" height="100vh" />;
	}

	if (!userDetail) {
		return <Navigate to="/login" replace />;
	}

	if (!userDetail?.privacyPolicyAcknowledged) {
		return <ConsentPopup userDetail={userDetail} refetchUserDetail={refetch} />;
	}

	return (
		<div className={`layout ${windowType === "windowIframe" ? "layout-embedded" : ""} ${isSidebarOpen ? "mobile-sidebar-open" : ""}`}>
			{windowType === "windowIframe" ? null : <Header logo={logo} showMenu={showSidebar} isSidebarOpen={isSidebarOpen} onToggleSidebar={() => setIsSidebarOpen((open) => !open)} />}
			{showSidebar ? <Sidebar /> : null}

			<main>{children ? children : <Outlet />}</main>
			<GuidedTour />
		</div>
	);
}

function Header({ logo, showMenu, isSidebarOpen, onToggleSidebar }) {
	const { plantOptions } = usePlantContext();
	const { userPermissions } = useUserPermissions();
	const activeUrl = localStorage.getItem("activeURL");
	return (
		<header>
			<div className="logo">
				{showMenu ? (
					<button className="mobile_menu_btn" onClick={onToggleSidebar} aria-label={isSidebarOpen ? "Close navigation menu" : "Open navigation menu"} aria-controls="sidebar-navigation" aria-expanded={isSidebarOpen}>
						{isSidebarOpen ? <CloseIcon /> : <MenuIcon />}
					</button>
				) : null}
				<Link to={activeUrl ? activeUrl : "/login"}>
					{/* An explicit plant override of `null` hides the logo entirely — no LynkIdlogo fallback. */}
					{logo ? <img src={logo} alt="" className="" /> : null}
				</Link>
			</div>
			<div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
				{userPermissions?.manageTrip?.value ? <VehicleSearchDropdown /> : null}
				{Array.isArray(plantOptions) && plantOptions.length === 1 ? <></> : <TogglePlant />}
				{/* <TogglePlant/> */}
				<Profile />
			</div>
		</header>
	);
}

export function TogglePlant({ alwaysVisible = false }) {
	const { plantId, plantOptions, loading, updatePlantId, isCheckpointForm } = usePlantContext();
	const { isSuperAdmin } = useUserDetail();
	const location = useLocation();
	const path = location.pathname;
	const selectedOption = plantOptions.find((opt) => opt.value === plantId) || plantOptions[0] || null;
	const modulesToExclude = ["/vehicle-movement-stats", "/device-map", "/plants", "/plants/add-plant", "/reports", "/driver-master", "/vehicle-master", "/users", "/users/add-user-form", "/users/edit-user-form", "/roles", "/allTrips", "/settings", "/plants/edit-plant", "/third-party-logs"];
	if (!alwaysVisible && (isSuperAdmin || modulesToExclude.includes(path))) return null;

	return (
		<div className="warehouse_selector" style={{ minWidth: "200px", marginTop:"4px" }}>
			<Dropdown
				label="Select Warehouse"
				options={plantOptions || []}
				value={selectedOption}
				onChange={updatePlantId}
				disabled={loading || !plantOptions || plantOptions.length === 0}
				disableClearable={true}
				readonly={isCheckpointForm || ["add", "edit", "map-view"].some((keyword) => path.includes(keyword))}
			/>
		</div>
	);
}

function Profile() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { userPermissions } = useUserPermissions();
	const [isMenu, setIsMenu] = useState(false);
	const [isPlantSwitchOpen, setIsPlantSwitchOpen] = useState(false);
	const handleMenuOpen = () => setIsMenu(true);
	const [, setReceivedGuidedTour] = useTour();
	const { plantOptions, plantName } = usePlantContext();
	const profileRef = useRef();
	useClickOutside(profileRef, () => setIsMenu(false));
	const hasSettingsPermission = userPermissions?.manageTheme?.value || userPermissions?.modifyTripPrefix?.value || userPermissions?.customizeLabels?.value || userPermissions?.manageRules?.value || userPermissions?.manageRouteValidations?.value || userPermissions?.manageAlerts?.value;
	const { userDetail } = useUserDetail();
	const initial = userDetail?.name ? userDetail.name[0].toUpperCase() : "G";
	const name = userDetail?.name ? userDetail?.name.split(" ")[0] : "";
	const { renderedMode, setPersonalMode } = useBranding();
	const currentTheme = renderedMode;
	const themeActionLabel = currentTheme === "darkMode" ? "Light Mode" : "Dark Mode";

	const [updateTour, { isLoading: isUpdating }] = useUpdateTourStatusMutation();
	const [logoutMutation] = useLogoutMutation();

	const onLogOut = async () => {
		try {
			await logoutMutation();
			socket.disconnect();
			let project = localStorage.getItem("project");
			let personalModeKey = userDetail?.userId ? `themeMode:${userDetail.userId}` : null;
			let personalMode = personalModeKey ? localStorage.getItem(personalModeKey) : null;
			localStorage.clear();
			if (personalModeKey && personalMode) localStorage.setItem(personalModeKey, personalMode); // Preserving this user's mode choice during logout to avoid a light-mode flicker on next login.
			dispatch(apiSlice.util.resetApiState()); // Clear cached branding/session state so a different account never sees this one's cache.
			if (project === "WMS") {
				window.location.replace(window.localStorage.getItem("baseURL"));
			} else {
				window.location.reload();
			}
		} catch (error) {
			console.error("Logout failed:", error);
			// Handle error (show toast, etc.)
		}
	};

	const onReplayTour = () => {
		updateTour({ receivedGuidedTour: false, userId: userDetail?.userId })
			.unwrap()
			.then((res) => {
				if (!res.error) {
					setReceivedGuidedTour(false);
				}
			});
	};
	const [isChangePass, setIsChangePass] = useState(false);
	const handleChangePassOpen = () => setIsChangePass(true);
	const handleChangePassClose = () => setIsChangePass(false);
	const onToggleTheme = () => {
		const nextTheme = currentTheme === "darkMode" ? "lightMode" : "darkMode";
		setPersonalMode(nextTheme);
		setIsMenu(false);
	};
	const handlePlantSwitch = () => {
		setIsMenu(false);
		setIsPlantSwitchOpen(true);
	};
	const handlePlantSwitchClose = () => setIsPlantSwitchOpen(false);

	return (
		<>
			<div className="user_panel" ref={profileRef}>
				<button className="profile_btn" onClick={handleMenuOpen}>
					{initial}
				</button>
				{isMenu ? (
					<ul>
						<li className="profile_detail">
							<div className="profile_btn">{initial}</div>
							<div className="profile_info">
								<div className="profile_name">{name || "Guest"}</div>
								<div className="profile_mob">{userDetail?.mobile || "NA"}</div>
							</div>
						</li>
						{userPermissions.changePassword?.value ? 
                            <li>
                                <button onClick={handleChangePassOpen}>Change Password</button>
                            </li> 
                            :null
                        }
						{ hasSettingsPermission  ? (
							<li>
								<button onClick={() => navigate("/settings")}>Settings</button>
							</li>
						) : null}
						<li>
							<button onClick={onToggleTheme}>{themeActionLabel}</button>
						</li>
						<li>
							<button onClick={onReplayTour}>Replay Tour</button>
						</li>
						{Array.isArray(plantOptions) && plantOptions.length === 1 ? <></> :
							<li>
								<button onClick={handlePlantSwitch}>Change Warehouse</button>

							</li>
						}
						<li>
							<button onClick={onLogOut}>Log Out</button>
						</li>
					</ul>
				) : null}

				{isChangePass && userPermissions?.changePassword?.value ? <ChangePassword open={isChangePass} onClose={handleChangePassClose} /> : null}
			</div>
			<Dialog open={isPlantSwitchOpen} handleClose={handlePlantSwitchClose} title="Change Warehouse" size="xs" CancelText="Close" hideSubmitButton>
				<TogglePlant alwaysVisible />
				<div style={{ marginTop: "0.75rem", color: "var(--text-color)" }}>Selected Warehouse: "{plantName}"</div>
			</Dialog>
		</>
	);
}

function ChangePassword({ open, onClose }) {
	const [formData, setFormData] = useState({
		password: "",
		newPassword: "",
		confirmNewPassword: "",
	});
	const handleInput = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};
	const [handleSubmit, { isLoading }] = useChangePasswordMutation();

	const handleFormSubmit = async () => {
		const payload = { ...formData };
		delete payload.confirmNewPassword;
		const {
			data: { success, message },
		} = await handleSubmit(payload);
		if (!success) {
			toastMessage(false, message);
		} else {
			toastMessage(true, message);
			handleClose();
			socket.disconnect();
			localStorage.removeItem("secretkey");
			localStorage.removeItem("primaryColor");
			localStorage.removeItem("selectedTheme");
			localStorage.removeItem("favicon");
			localStorage.removeItem("title");
			localStorage.removeItem("logo");
			localStorage.removeItem("project");
			window.location.reload();
		}
	};
	const handleClose = () => {
		onClose();
	};
	return (
		<Dialog open={open} handleClose={handleClose} handleSubmit={handleFormSubmit} title="Change Password" size="sm" isSubmitDisabled={isLoading}>
			<form className="form-inputs">
				<Input label="Old Password" placeholder="Enter Old Password" name="password" value={formData.password} onChange={handleInput} required />
				<Input label="New Password" placeholder="Enter New Password" name="newPassword" value={formData.newPassword} onChange={handleInput} required />
				<Input label="Confirm Password" placeholder="Confirm New Password" name="confirmNewPassword" value={formData.confirmNewPassword} onChange={handleInput} required />
			</form>
		</Dialog>
	);
}

function Sidebar() {
	const [isActiveChild, setIsActiveChild] = useState("");
	const { setIsCheckpointForm } = usePlantContext();
	const { sidebarItems, search, setSearch } = useSidebarItems();

	return (
		<aside id="sidebar-navigation">
			<div className="sidebar_search">
				<Input
					id="search_sidebar"
					name="item"
					placeholder="Search.."
					// Label="Search..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ borderRadius: "12px", height: "1.6rem" }}
					adornment={{
						is: true,
						type: "CloseIcon",
						onClick: () => {
							setSearch("");
						},
					}}
				/>
			</div>
			<ul>
				{sidebarItems?.map((item, i) => {
					if (!item.permission) return null;
					return (
						<li
							key={i}
							className="sidebar_item"
							onMouseEnter={() => {
								let elem = document.getElementById(item.title);
								elem.style.height = `${elem.scrollHeight}px`;
							}}
							onMouseLeave={() => {
								let elem = document.getElementById(item.title);
								elem.style.height = `0px`;
							}}
							onClick={() => {
								let elem = document.getElementById(item.title);
								elem.style.height = elem.clientHeight === 0 ? `${elem.scrollHeight}px` : "0px";
							}}
						>
							{item.children.length > 0 ? (
								<Link to="." onClick={(event) => event.preventDefault()} className={Boolean(item.children.find((ele) => ele.title === isActiveChild)) ? "navlink childActive" : "navlink"}>
									<item.Icon fontSize="small" className="sidebar_icon" />
									<span className={`sidebar_title ${item.class || ""}`}>{item.title}</span>
									<RightArrowIcon fontSize="small" className="sidebar_icon arrowIcon" />
								</Link>
							) : (
								<NavLink to={item.link} end state={item.state} className={({ isActive }) => (isActive ? "navlink active" : "navlink")} onClick={() => {setIsActiveChild(""); setIsCheckpointForm(false);}}>
									<item.Icon fontSize="small" className="sidebar_icon" />
									<span className={`sidebar_title ${item.class || ""}`}>{item.title}</span>
								</NavLink>
							)}

							<div className="sidebar_children" id={item.title}>
								{item.children?.map((child, indx) => {
									if (!child.permission) return null;
									return (
										<NavLink
											key={indx}
											to={child.link}
											state={child.state}
											style={{ paddingLeft: "3rem" }}
											className={({ isActive }) => (isActive ? "navlink active" : "navlink")}
											onClick={() => {setIsActiveChild(child.title); setIsCheckpointForm(false);}}
										>
											<span className="sidebar_title">{child.title}</span>
										</NavLink>
									);
								})}
							</div>
						</li>
					);
				})}
			</ul>
		</aside>
	);
}

function ChatBot() {
	const [isChat, setIsChat] = useState(false);

	const [input, setInput] = useState("");
	const messagesEndRef = useRef(null);

	const { messages, sendMessage } = useChatbot();

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	const handleSendMessage = () => {
		if (!input.trim()) return;
		sendMessage(input);
		setInput("");
	};

	return (
		<>
			<div className="chatbot_wrap">
				{isChat ? (
					<div className="chat-container">
						<div className="chat-header">
							<span>Support Chat</span>
							<CloseIcon
								className="chatbot_close"
								onClick={() => {
									setIsChat(false);
								}}
							/>
						</div>
						<div className="chat-messages">
							{messages.map((msg, index) => (
								<div key={index} className={`message ${msg.sender}`}>
									{msg.text}
								</div>
							))}
							<div ref={messagesEndRef} />
						</div>
						<div className="chat-input-container">
							<input
								type="text"
								className="chat-input"
								placeholder="Type a message..."
								value={input}
								onChange={(e) => setInput(e.target.value)}
								onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
							/>
							<button className="send-button" onClick={handleSendMessage}>
								<SendIcon className="chatbot_send_icon" />
							</button>
						</div>
					</div>
				) : (
					<AssistantIcon
						className="chatbot_icon"
						onClick={() => {
							setIsChat(true);
						}}
					/>
				)}
			</div>
		</>
	);
}
